import { 
  Component, 
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  ViewChild,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  Validators 
} from '@angular/forms';

import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { isEqual, isNil, isEmpty } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatexFormControl } from './models/datex-form-control';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  SeparatorModel,
  ButtonStyles 
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { ToolModel } from './models/tool';
import { TabItemModel, TabGroupModel } from './models/tab';
import { WidgetModel } from './models/widget';
import { FieldsetModel } from './models/fieldset';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { FootprintManager_ShellService } from './FootprintManager.shell.service';
import { FootprintManager_OperationService } from './FootprintManager.operation.service';
import { FootprintManager_DatasourceService } from './FootprintManager.datasource.index';
import { FootprintManager_FlowService } from './FootprintManager.flow.index';
import { FootprintManager_ReportService } from './FootprintManager.report.index';
import { FootprintManager_LocalizationService } from './FootprintManager.localization.service';
import { FootprintManager_single_material_hub_ComponentContextService } from './FootprintManager.single_material_hub.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './FootprintManager.frontend.types'
import { $frontendTypes as $types} from './FootprintManager.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

import { FootprintManager_inventory_location_gridComponent } from './FootprintManager.inventory_location_grid.component';
import { FootprintManager_inventory_licenseplate_gridComponent } from './FootprintManager.inventory_licenseplate_grid.component';
import { FootprintManager_inventory_lot_gridComponent } from './FootprintManager.inventory_lot_grid.component';
import { FootprintManager_inventory_material_gridComponent } from './FootprintManager.inventory_material_grid.component';
import { FootprintManager_licenseplate_listComponent } from './FootprintManager.licenseplate_list.component';
import { FootprintManager_inventory_tasks_gridComponent } from './FootprintManager.inventory_tasks_grid.component';
import { FootprintManager_material_packagings_gridComponent } from './FootprintManager.material_packagings_grid.component';
import { FootprintManager_material_kits_gridComponent } from './FootprintManager.material_kits_grid.component';
import { FootprintManager_materials_udf_gridComponent } from './FootprintManager.materials_udf_grid.component';
import { FootprintManager_serialnumbers_gridComponent } from './FootprintManager.serialnumbers_grid.component';
import { FootprintManager_locations_replenishments_gridComponent } from './FootprintManager.locations_replenishments_grid.component';
import { FootprintManager_primary_picks_gridComponent } from './FootprintManager.primary_picks_grid.component';
import { Inventory_total_available_amount_widgetComponent } from './Inventory.total_available_amount_widget.component';
import { Inventory_total_available_base_amount_widgetComponent } from './Inventory.total_available_base_amount_widget.component';
import { Inventory_total_gross_weight_widgetComponent } from './Inventory.total_gross_weight_widget.component';
import { Inventory_inventory_aggregation_dd_singleComponent } from './Inventory.inventory_aggregation_dd_single.component'

@Component({
  standalone: true,
  imports: [
    SharedModule,
    forwardRef(() => FootprintManager_inventory_location_gridComponent),
    forwardRef(() => FootprintManager_inventory_licenseplate_gridComponent),
    forwardRef(() => FootprintManager_inventory_lot_gridComponent),
    forwardRef(() => FootprintManager_inventory_material_gridComponent),
    forwardRef(() => FootprintManager_licenseplate_listComponent),
    forwardRef(() => FootprintManager_inventory_tasks_gridComponent),
    forwardRef(() => FootprintManager_material_packagings_gridComponent),
    forwardRef(() => FootprintManager_material_kits_gridComponent),
    forwardRef(() => FootprintManager_materials_udf_gridComponent),
    forwardRef(() => FootprintManager_serialnumbers_gridComponent),
    forwardRef(() => FootprintManager_locations_replenishments_gridComponent),
    forwardRef(() => FootprintManager_primary_picks_gridComponent),
    forwardRef(() => Inventory_total_available_amount_widgetComponent),
    forwardRef(() => Inventory_total_available_base_amount_widgetComponent),
    forwardRef(() => Inventory_total_gross_weight_widgetComponent),
    forwardRef(() => Inventory_inventory_aggregation_dd_singleComponent),
  ],
  selector: 'FootprintManager-single_material_hub',
  templateUrl: './FootprintManager.single_material_hub.component.html'
})
export class FootprintManager_single_material_hubComponent extends BaseComponent implements OnInit, OnDestroy, OnChanges {

  inParams: { materialId: number } = { materialId: null };
  //#region Inputs
  @Input('materialId') set $inParams_materialId(v: number) {
    this.inParams.materialId = v;
  }
  get $inParams_materialId(): number {
    return this.inParams.materialId;
  }
  //#endregion Inputs

  @Input() showInDialog: boolean = false; 
  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  @Output()
  $refreshEvent = new EventEmitter();  
  //#endregion Outputs
  hasToolbar: boolean = true;



  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  vars: { hubTitle?: string, hubDescription?: string, project_id?: number, lookupCode?: string } = { };
  //#endregion
  formGroup: FormGroup = new FormGroup({
    inventory_type: new DatexFormControl(null, { validators: [  ], updateOn: 'blur' }),
  });
  
  toolbar = {
      update: new ToolModel(new ButtonModel('update', new ButtonStyles(null, null), false, false, false, 'Edit', 'icon-ic_fluent_edit_20_regular', null)
    , false),
      separator1: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      create_inventory: new ToolModel(new ButtonModel('create_inventory', new ButtonStyles(['creation'], null), false, false, false, 'Create inventory', 'icon-ic_fluent_add_20_regular', null)
    , false),
      separator2: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      on_delete: new ToolModel(new ButtonModel('on_delete', new ButtonStyles(['destructive'], null), false, false, false, 'Delete', 'icon-ic_fluent_delete_20_regular', null)
    , false),
      separator3: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      attachments: new ToolModel(new ButtonModel('attachments', new ButtonStyles(null, null), false, false, false, ' ', 'icon-ic_fluent_attach_20_regular', null)
    , false),
      discussions: new ToolModel(new ButtonModel('discussions', new ButtonStyles(null, null), false, false, false, ' ', 'icon-ic_fluent_chat_20_regular', null)
    , false),
      print: new ToolModel(new ButtonModel('print', new ButtonStyles(null, null), false, false, false, ' ', 'icon-ic_fluent_print_20_regular', null)
    , false),
      separator4: new ToolModel(new SeparatorModel(new Styles(null, null))
    , false),
      options: new ToolModel(new SplitButtonModel<{ instructions: ButtonModel }>(
        'options',
        new ButtonStyles(null, null),
        false,
        false,
        ' ',
        'icon-ic_fluent_more_horizontal_20_regular',
        null,
        [
          new ButtonModel('instructions', new ButtonStyles(null, null), false, false, false, 'Instructions', 'icon-ic_fluent_book_information_20_regular')
        ])
    , false)
  };

  actionbar = {
      assign_containers_button: new ToolModel(new ButtonModel('assign_containers_button', new ButtonStyles(null, null), false, false, false, 'Assign containers', 'icon-ic_fluent_text_bullet_list_20_regular', null)
    , false),
      material_substitutions_button: new ToolModel(new ButtonModel('material_substitutions_button', new ButtonStyles(null, null), false, false, false, 'Material substitutions', 'icon-ic_fluent_flowchart_20_regular', null)
    , false),
      material_aliases: new ToolModel(new ButtonModel('material_aliases', new ButtonStyles(null, null), false, false, false, 'Material aliases', 'icon-ic_fluent_rename_20_regular', null)
    , false)
  };

 filters = {
    inventory_type: new FieldModel(new SelectBoxModel(
  this.formGroup.controls['inventory_type'] as DatexFormControl, 
  ESelectBoxType.chips, null,
  false,
  false,
  '', null)
, new ControlContainerStyles(null, null), 'Inventory type', false, false),
  };


  filtersets = {
    newGroup1: new FieldsetModel(
      'Label of newGroup1',
      true,
      false,
      true,
      false,
        null
      ,
      () => {
        const $hub = this;
        const $utils = this.$utils;
        return '';
      }
    ),
};

    rootTabGroup = new TabGroupModel();
  
    subTabGroups = {
      inventory: new TabGroupModel(),
    };
  
    onTabSelected(event: MatSelectChange) {
      event.value.activate();
    }
  
    tabs = {
      inventory: new TabItemModel(
        this.rootTabGroup, 
        'Inventory', 
        false,
        true,
        {
          inventory_by_location: new TabItemModel(
          this.subTabGroups.inventory, 
          'Inventory', 
          false
          ),
          inventory_by_lp: new TabItemModel(
          this.subTabGroups.inventory, 
          'Inventory', 
          false
          ),
          inventory_by_lot: new TabItemModel(
          this.subTabGroups.inventory, 
          'Inventory', 
          false
          ),
          inventory_by_material: new TabItemModel(
          this.subTabGroups.inventory, 
          'Inventory', 
          false
          ),
        }
        ),
      licenseplates: new TabItemModel(
        this.rootTabGroup, 
        'License plates', 
        false,
        ),
      tasks: new TabItemModel(
        this.rootTabGroup, 
        'Activity', 
        false,
        ),
      packagings: new TabItemModel(
        this.rootTabGroup, 
        'Packagings', 
        false,
        ),
      material_kits: new TabItemModel(
        this.rootTabGroup, 
        'Material Kits', 
        false,
        ),
      udfs: new TabItemModel(
        this.rootTabGroup, 
        'Custom fields', 
        false,
        ),
      serialnumbers: new TabItemModel(
        this.rootTabGroup, 
        'Serial numbers', 
        false,
        ),
      replenishments: new TabItemModel(
        this.rootTabGroup, 
        'Replenishments', 
        false,
        ),
      primary_picks: new TabItemModel(
        this.rootTabGroup, 
        'Primary picks', 
        false,
        ),
    };
  
  
    //#region tabs inParams
    cacheValueFor_$tabs_inventory_inventory_by_location_inventory_location_grid_inParams_materialIds: number[];
    get $tabs_inventory_inventory_by_location_inventory_location_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_inventory_inventory_by_location_inventory_location_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_inventory_inventory_by_location_inventory_location_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_inventory_inventory_by_location_inventory_location_grid_inParams_materialIds;
    }
  
    cacheValueFor_$tabs_inventory_inventory_by_lp_inventory_licenseplate_grid_inParams_materialIds: number[];
    get $tabs_inventory_inventory_by_lp_inventory_licenseplate_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_inventory_inventory_by_lp_inventory_licenseplate_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_inventory_inventory_by_lp_inventory_licenseplate_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_inventory_inventory_by_lp_inventory_licenseplate_grid_inParams_materialIds;
    }
  
    cacheValueFor_$tabs_inventory_inventory_by_lot_inventory_lot_grid_inParams_materialIds: number[];
    get $tabs_inventory_inventory_by_lot_inventory_lot_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_inventory_inventory_by_lot_inventory_lot_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_inventory_inventory_by_lot_inventory_lot_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_inventory_inventory_by_lot_inventory_lot_grid_inParams_materialIds;
    }
  
    cacheValueFor_$tabs_inventory_inventory_by_material_inventory_material_grid_inParams_materialIds: number[];
    get $tabs_inventory_inventory_by_material_inventory_material_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_inventory_inventory_by_material_inventory_material_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_inventory_inventory_by_material_inventory_material_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_inventory_inventory_by_material_inventory_material_grid_inParams_materialIds;
    }
  
    get $tabs_licenseplates_licenseplate_list_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $tabs_tasks_inventory_tasks_grid_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $tabs_packagings_material_packagings_grid_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $tabs_material_kits_material_kits_grid_inParams_material_id(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $tabs_material_kits_material_kits_grid_inParams_project_id(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.vars.project_id;
      
      return expr;
    }
  
    cacheValueFor_$tabs_udfs_materials_udf_grid_inParams_materialIds: number[];
    get $tabs_udfs_materials_udf_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_udfs_materials_udf_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_udfs_materials_udf_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_udfs_materials_udf_grid_inParams_materialIds;
    }
  
    cacheValueFor_$tabs_serialnumbers_serialnumbers_grid_inParams_materialIds: number[];
    get $tabs_serialnumbers_serialnumbers_grid_inParams_materialIds(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_serialnumbers_serialnumbers_grid_inParams_materialIds, expr)) {
        this.cacheValueFor_$tabs_serialnumbers_serialnumbers_grid_inParams_materialIds = expr;
      }
      return this.cacheValueFor_$tabs_serialnumbers_serialnumbers_grid_inParams_materialIds;
    }
  
    get $tabs_serialnumbers_serialnumbers_grid_inParams_archived(): boolean {
      const $hub = this;
      const $utils = this.$utils;
      const expr = false;
      
      return expr;
    }
  
    cacheValueFor_$tabs_replenishments_locations_replenishments_grid_inParams_materialId: number[];
    get $tabs_replenishments_locations_replenishments_grid_inParams_materialId(): number[] {
      const $hub = this;
      const $utils = this.$utils;
      const expr = [$hub.inParams.materialId];
      
      if(!isEqual(this.cacheValueFor_$tabs_replenishments_locations_replenishments_grid_inParams_materialId, expr)) {
        this.cacheValueFor_$tabs_replenishments_locations_replenishments_grid_inParams_materialId = expr;
      }
      return this.cacheValueFor_$tabs_replenishments_locations_replenishments_grid_inParams_materialId;
    }
  
    get $tabs_primary_picks_primary_picks_grid_inParams_material_id(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    //#endregion tabs inParams
  
    //#region tabs children
        @ViewChild('$tabs_inventory_inventory_by_location', { read: FootprintManager_inventory_location_gridComponent }) $tabs_inventory_inventory_by_location: FootprintManager_inventory_location_gridComponent;
        @ViewChild('$tabs_inventory_inventory_by_lp', { read: FootprintManager_inventory_licenseplate_gridComponent }) $tabs_inventory_inventory_by_lp: FootprintManager_inventory_licenseplate_gridComponent;
        @ViewChild('$tabs_inventory_inventory_by_lot', { read: FootprintManager_inventory_lot_gridComponent }) $tabs_inventory_inventory_by_lot: FootprintManager_inventory_lot_gridComponent;
        @ViewChild('$tabs_inventory_inventory_by_material', { read: FootprintManager_inventory_material_gridComponent }) $tabs_inventory_inventory_by_material: FootprintManager_inventory_material_gridComponent;
      @ViewChild('$tabs_licenseplates', { read: FootprintManager_licenseplate_listComponent }) $tabs_licenseplates: FootprintManager_licenseplate_listComponent;
      @ViewChild('$tabs_tasks', { read: FootprintManager_inventory_tasks_gridComponent }) $tabs_tasks: FootprintManager_inventory_tasks_gridComponent;
      @ViewChild('$tabs_packagings', { read: FootprintManager_material_packagings_gridComponent }) $tabs_packagings: FootprintManager_material_packagings_gridComponent;
      @ViewChild('$tabs_material_kits', { read: FootprintManager_material_kits_gridComponent }) $tabs_material_kits: FootprintManager_material_kits_gridComponent;
      @ViewChild('$tabs_udfs', { read: FootprintManager_materials_udf_gridComponent }) $tabs_udfs: FootprintManager_materials_udf_gridComponent;
      @ViewChild('$tabs_serialnumbers', { read: FootprintManager_serialnumbers_gridComponent }) $tabs_serialnumbers: FootprintManager_serialnumbers_gridComponent;
      @ViewChild('$tabs_replenishments', { read: FootprintManager_locations_replenishments_gridComponent }) $tabs_replenishments: FootprintManager_locations_replenishments_gridComponent;
      @ViewChild('$tabs_primary_picks', { read: FootprintManager_primary_picks_gridComponent }) $tabs_primary_picks: FootprintManager_primary_picks_gridComponent;
    //#endregion tabs children
    widgets = {
      total_available_amount_widget: new WidgetModel(false),
      total_available_base_amount_widget: new WidgetModel(false),
      total_gross_weight_widget: new WidgetModel(false),
    };
  
    //#region widgets inParams
    get $widgets_total_available_amount_widget_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $widgets_total_available_base_amount_widget_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    get $widgets_total_gross_weight_widget_inParams_materialId(): number {
      const $hub = this;
      const $utils = this.$utils;
      const expr = $hub.inParams.materialId;
      
      return expr;
    }
  
    //#endregion widgets inParams
  
    //#region widgets children
      @ViewChild('$widgets_total_available_amount_widget', { read:  Inventory_total_available_amount_widgetComponent }) $widgets_total_available_amount_widget: Inventory_total_available_amount_widgetComponent;
      @ViewChild('$widgets_total_available_base_amount_widget', { read:  Inventory_total_available_base_amount_widgetComponent }) $widgets_total_available_base_amount_widget: Inventory_total_available_base_amount_widgetComponent;
      @ViewChild('$widgets_total_gross_weight_widget', { read:  Inventory_total_gross_weight_widgetComponent }) $widgets_total_gross_weight_widget: Inventory_total_gross_weight_widgetComponent;
    //#endregion widgets children

  //#region filters inParams
  //#endregion filters inParams

  get hubTitle(): string {
    const $hub = this;
    const $utils = this.$utils;
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //const $l10n = this.localization;
    return $hub.vars.hubTitle;
  }

  get hubDescription(): string {
    const $hub = this;
    const $utils = this.$utils;
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //const $l10n = this.localization;
    return $hub.vars.hubDescription;
  }

  constructor(
  private $utils: UtilsService,
  private $settings: SettingsValuesService,
  private $shell: FootprintManager_ShellService,
  private $datasources: FootprintManager_DatasourceService,
  private $flows: FootprintManager_FlowService,
  private $reports: FootprintManager_ReportService,
  private $localization: FootprintManager_LocalizationService,
  private $operations: FootprintManager_OperationService,
  private $logger: CleanupLoggerService,
  private $context: FootprintManager_single_material_hub_ComponentContextService,
  ) { 
    super();
    this.$subscribeFormControlValueChanges();
    this.hasToolbar = !isEmpty(this.toolbar);

    //#region tabs tab init
    this.rootTabGroup.tabs = [
      this.tabs.inventory,
      this.tabs.licenseplates,
      this.tabs.tasks,
      this.tabs.packagings,
      this.tabs.material_kits,
      this.tabs.udfs,
      this.tabs.serialnumbers,
      this.tabs.replenishments,
      this.tabs.primary_picks,
    ]; 
    this.subTabGroups.inventory.tabs = [
        this.tabs.inventory.tabs.inventory_by_location,
        this.tabs.inventory.tabs.inventory_by_lp,
        this.tabs.inventory.tabs.inventory_by_lot,
        this.tabs.inventory.tabs.inventory_by_material,
    ];    
    //#endregion tabs tab init
  }

  ngOnInit(): void {
    this.$checkRequiredInParams();
    if (!this.$hasMissingRequiredInParams) {
      this.$init();
    } else {
      this.$initEmpty();
    }
  }
  
  private $isFirstNgOnChanges = true;
  ngOnChanges(changes: SimpleChanges): void {
    if (this.$isFirstNgOnChanges) {
      this.$isFirstNgOnChanges = false;
    } else {
      this.$checkRequiredInParams();
      if(!this.$hasMissingRequiredInParams) {
        this.$init();
      } else {
        this.$initEmpty();
      }
    }
  }

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }
  $missingRequiredInParams = [];
  get $hasMissingRequiredInParams(): boolean {
    return !!this.$missingRequiredInParams.length;
  }
  
  $checkRequiredInParams() {
    this.$missingRequiredInParams = [];
      if(isNil(this.inParams.materialId)) {
        this.$missingRequiredInParams.push('materialId');
      }
  }



  initialized = false;

  async $init() {
    this.title = 'Single Material Hub';
    
    const $hub = this;
    const $utils = this.$utils;

    (this.filters.inventory_type.control as SelectBoxModel).reset(4);

    await this.on_init();

    this.initialized = true;
  }

  private $subscribeFormControlValueChanges() {
    this.formGroup
      .controls['inventory_type']
      .valueChanges
      .pipe(
        takeUntil(this.$unsubscribe$)
      )
      .subscribe(() => {
        this.on_inventory_type_change();
      });
  }
  close() {
    this.$finish.emit();
  }

  refresh(
    skipParent = false,
    skipChildren = false,
    childToSkip: string = null) {
    if (this.$hasMissingRequiredInParams) {
      return Promise.resolve(null);
    }
    // up
    if (skipParent === false) {
      this.$refreshEvent.emit();
    }

    // self
    const result = Promise.resolve(null);
    
    // children
    if (skipChildren === false) {
      this.$refreshChildren(childToSkip);
    }

    return result;
  }

  $refreshChildren(childToSkip: string) {
    //#region widgets children
    if (childToSkip !== '$widgets_total_available_amount_widget') {
      if (!isNil(this.$widgets_total_available_amount_widget) && !this.widgets.total_available_amount_widget.hidden) {
        this.$widgets_total_available_amount_widget.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$widgets_total_available_base_amount_widget') {
      if (!isNil(this.$widgets_total_available_base_amount_widget) && !this.widgets.total_available_base_amount_widget.hidden) {
        this.$widgets_total_available_base_amount_widget.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$widgets_total_gross_weight_widget') {
      if (!isNil(this.$widgets_total_gross_weight_widget) && !this.widgets.total_gross_weight_widget.hidden) {
        this.$widgets_total_gross_weight_widget.refresh(true, false, null);
      }
    }
    //#endregion widgets children
    //#region tabs children
      if (childToSkip !== '$tabs_inventory_inventory_by_location') {
        if (!isNil(this.$tabs_inventory_inventory_by_location) && !this.tabs.inventory.tabs.inventory_by_location.hidden) {
          this.$tabs_inventory_inventory_by_location.refresh(true, false, null);
        }
      }   
      if (childToSkip !== '$tabs_inventory_inventory_by_lp') {
        if (!isNil(this.$tabs_inventory_inventory_by_lp) && !this.tabs.inventory.tabs.inventory_by_lp.hidden) {
          this.$tabs_inventory_inventory_by_lp.refresh(true, false, null);
        }
      }   
      if (childToSkip !== '$tabs_inventory_inventory_by_lot') {
        if (!isNil(this.$tabs_inventory_inventory_by_lot) && !this.tabs.inventory.tabs.inventory_by_lot.hidden) {
          this.$tabs_inventory_inventory_by_lot.refresh(true, false, null);
        }
      }   
      if (childToSkip !== '$tabs_inventory_inventory_by_material') {
        if (!isNil(this.$tabs_inventory_inventory_by_material) && !this.tabs.inventory.tabs.inventory_by_material.hidden) {
          this.$tabs_inventory_inventory_by_material.refresh(true, false, null);
        }
      }   
    if (childToSkip !== '$tabs_licenseplates') {
      if (!isNil(this.$tabs_licenseplates) && !this.tabs.licenseplates.hidden) {
        this.$tabs_licenseplates.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_tasks') {
      if (!isNil(this.$tabs_tasks) && !this.tabs.tasks.hidden) {
        this.$tabs_tasks.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_packagings') {
      if (!isNil(this.$tabs_packagings) && !this.tabs.packagings.hidden) {
        this.$tabs_packagings.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_material_kits') {
      if (!isNil(this.$tabs_material_kits) && !this.tabs.material_kits.hidden) {
        this.$tabs_material_kits.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_udfs') {
      if (!isNil(this.$tabs_udfs) && !this.tabs.udfs.hidden) {
        this.$tabs_udfs.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_serialnumbers') {
      if (!isNil(this.$tabs_serialnumbers) && !this.tabs.serialnumbers.hidden) {
        this.$tabs_serialnumbers.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_replenishments') {
      if (!isNil(this.$tabs_replenishments) && !this.tabs.replenishments.hidden) {
        this.$tabs_replenishments.refresh(true, false, null);
      }
    }
    if (childToSkip !== '$tabs_primary_picks') {
      if (!isNil(this.$tabs_primary_picks) && !this.tabs.primary_picks.hidden) {
        this.$tabs_primary_picks.refresh(true, false, null);
      }
    }
    //#endregion tabs children
  }

  openImageViewer(imageSource: string) {
    this.$shell.openImageViewerDialog(imageSource);
  }

  //#region private flows
  on_create_inventory_clicked(event = null) {
    return this.on_create_inventory_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_create_inventory_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  const dialogResult = await $shell.FootprintManager.openinventory_creation_formDialog({
      materialId: $hub.inParams.materialId
  });
  const userConfirmed = dialogResult.confirm;
  
  if (userConfirmed) {
      $hub.refresh()
  }
  
  
  }
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  
  const material = (await $datasources.Materials.ds_get_material_by_materialId.get({ materialId: $hub.inParams.materialId })).result;
  if ($utils.isDefined(material)) {
  
      $hub.vars.project_id = material[0].ProjectId;
  
      $hub.title = `Material ${material[0].LookupCode}`;
  
      const title = `Material ${material[0].LookupCode} Status ${material[0].Status.Name} `;
  
      if ($utils.isDefined(material[0].Description)) {
          const description = `${material[0].Description}  `;
          $hub.vars.hubDescription = description;
      }
  
      $hub.vars.hubTitle = title;
      $hub.vars.lookupCode = material[0].LookupCode;
  
  }
  
  // Hide serialnumbers tab if material isn't a serialized material. 
  if (!material[0].IsFixedVolume || !material[0].IsFixedWeight || material[0].ControllerTypeId === 3 || material[0].ControllerTypeId === 4) {
      $hub.tabs.serialnumbers.hidden = false;
  }
  else {
      $hub.tabs.serialnumbers.hidden = true;
  }
  
  // Initialize the inventory aggregation filter
  await $hub.on_inventory_type_change();
  
  
  
  // Lock down material editor if material is part of a catalog
  let versionCheck = await $flows.Utilities.check_footprint_version({ minimumVersion: '24.10.25' });
  
  if (versionCheck.meetsMinimumVersion) {
      // @ts-ignore 
      let catalog_material = (await $datasources.Catalogs.ds_get_material_catalog_details_by_material_id.get({ material_id: $hub.inParams.materialId })).result;
  
      if ($utils.isDefined(catalog_material) && $utils.isDefined(catalog_material[0]?.MaterialCatalogSourceMaterialId)) {
  
    
          $hub.toolbar.update.control.label = 'Catalog subscription'
          $hub.toolbar.update.control.icon = "ms-Icon ms-Icon--View"
  
          // Hide tabs and actions
          $hub.actionbar.assign_containers_button.hidden = true;
          $hub.actionbar.material_substitutions_button.hidden = true;
  
          $hub.tabs.material_kits.hidden = true;
          $hub.tabs.packagings.hidden = true;
          $hub.tabs.udfs.hidden = true;
  
          $hub.toolbar.on_delete.hidden = true;
  
  
      }
  }
  
  
  //Apply operations
  await $hub.on_apply_operations();
  
  
  }
  on_edit_clicked(event = null) {
    return this.on_edit_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_edit_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  
  
  
  const dialogResult = (await $shell.FootprintManager.openmaterial_editorDialog({materialId: $hub.inParams.materialId},'flyout',EModalSize.Large));
  const userConfirmed = dialogResult.confirm;
  
  if (userConfirmed) {
  
      await $hub.on_init();
      await $hub.refresh();
  }
  
  }
  on_delete(event = null) {
    return this.on_deleteInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_deleteInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  const result = await $flows.Materials.is_material_deletable_flow({ material_ids: [$hub.inParams.materialId] });
  const reason = result.reason;
  if ($utils.isDefined(reason)) {
      await $shell.FootprintManager.openErrorDialog('Material cannot be deleted.', reason);
      return;
  } else {
      const confirm = await $shell.FootprintManager.openConfirmationDialog('Delete Material', `Are you sure you want to delete the material?`, 'Yes', 'No');
      if (confirm) {
          try {
              const result = await $flows.Materials.delete_material_flow({ materialId: $hub.inParams.materialId });
              const reason = result.reason;
  
              if ($utils.isDefined(reason)) {
                  await $shell.FootprintManager.openErrorDialog('Material cannot be deleted.', reason);
                  return;
              }
              else {
                  $hub.close();
              }
          } catch (error) {
              const errorMessage = $utils.isDefined(error?.error?.error) ? error?.error?.error.message : error;
              const errorDetail = $utils.isDefined(error?.error?.error) ? error?.error?.error : error;
              const errorDescription = `${errorMessage}`;
              await $shell.FootprintManager.openErrorDialog('Material delete error', 'An error occurred during the deletion of the material', [errorDescription], null, [{ message: errorDescription, detail: errorDetail }]);
          }
      }
  }
  }
  on_print_clicked(event = null) {
    return this.on_print_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_print_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  await $shell.FootprintManager.openInfoDialog('Not yet implemented', 'The print functionality for this screen has not yet been implemented.');
  }
  on_attachments_clicked(event = null) {
    return this.on_attachments_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_attachments_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  await $shell.FootprintManager.openmaterial_attachments_hubDialog({ materialId: $hub.inParams.materialId },'flyout',EModalSize.Large);
  }
  on_assign_clicked(event = null) {
    return this.on_assign_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_assign_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  $shell.FootprintManager.opencontainer_type_assignment_gridDialog({materialId: $hub.inParams.materialId})
  }
  on_inventory_type_change(event = null) {
    return this.on_inventory_type_changeInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_inventory_type_changeInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  var inventoryType = $hub.filters.inventory_type.control.value;
  
  if (inventoryType == 1) {
      $hub.tabs.inventory.tabs.inventory_by_location.hidden = false;
      $hub.tabs.inventory.tabs.inventory_by_location.activate();
  
      $hub.tabs.inventory.tabs.inventory_by_lp.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_lot.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_material.hidden = true;
  }
  
  else if (inventoryType == 2) {
      $hub.tabs.inventory.tabs.inventory_by_lp.hidden = false;
      $hub.tabs.inventory.tabs.inventory_by_lp.activate();
  
      $hub.tabs.inventory.tabs.inventory_by_location.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_lot.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_material.hidden = true;
  
  
  }
  else if (inventoryType == 3) {
      $hub.tabs.inventory.tabs.inventory_by_lot.hidden = false;
      $hub.tabs.inventory.tabs.inventory_by_lot.activate();
  
      $hub.tabs.inventory.tabs.inventory_by_location.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_lp.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_material.hidden = true;
  
  }
  else if (inventoryType == 4) {
      $hub.tabs.inventory.tabs.inventory_by_material.hidden = false;
      $hub.tabs.inventory.tabs.inventory_by_material.activate();
  
      $hub.tabs.inventory.tabs.inventory_by_location.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_lp.hidden = true;
      $hub.tabs.inventory.tabs.inventory_by_lot.hidden = true;
  
  }
  }
  on_substitutions_clicked(event = null) {
    return this.on_substitutions_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_substitutions_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  
  const material = (await $datasources.Materials.ds_get_material_by_materialId.get({ materialId: $hub.inParams.materialId })).result;
  const project_id = material[0].ProjectId;
  
  await $shell.FootprintManager.openmaterial_substitutions_editorDialog({
      material_id: $hub.inParams.materialId,
      project_id: project_id
  }, "flyout", EModalSize.Large);
  
  
  
  
  }
  on_aliases_clicked(event = null) {
    return this.on_aliases_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_aliases_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  
  const material = (await $datasources.Materials.ds_get_material_by_materialId.get({ materialId: $hub.inParams.materialId })).result;
  const project_id = material[0].ProjectId;
  var materialCode = ''
  
  if (material[0]?.LookupCode.trim().toUpperCase() === material[0]?.Name.trim().toUpperCase()) {
      materialCode = material[0]?.LookupCode
  } else {
      materialCode = `${material[0]?.LookupCode} - ${material[0]?.Name}`
  }
  
  
  await $shell.FootprintManager.openmaterial_aliases_gridDialog({
      material_id: $hub.inParams.materialId,
      project_id: project_id, 
      materialCode: materialCode
  }, "modal", EModalSize.Large);
  
  
  
  
  }
  on_discussions_clicked(event = null) {
    return this.on_discussions_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_discussions_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  
  
  await $shell.Discussions.opendiscussions_listDialog({
      entity_name: 'Material',
      entity_id: $hub.inParams.materialId.toString()
  },
  'flyout')
  
  $hub.refresh();
  }
  on_apply_operations(event = null) {
    return this.on_apply_operationsInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_apply_operationsInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  $hub.toolbar.create_inventory.hidden = (await $operations.FootprintManager.Disable_Inventory_Creation.isAuthorized());
  $hub.actionbar.material_aliases.hidden = (await $operations.FootprintManager.Disable_Material_MaterialAliases.isAuthorized());
  
  
  // Discussions feature
  let discussionCheck = (await $operations.Discussions.Enable_Discussions_Feature.isAuthorized())
  
  if (discussionCheck) {
      const discussions = (await $datasources.Discussions.ds_storage_discussions.getList({
          entity_name: 'Material',
          entity_id: $hub.inParams.materialId.toString()
      })).result;
  
      if ($utils.isDefined(discussions)) {
  
          let containsUnread = false;
          let containsDiscussion = false;
  
          for (let discussion of discussions) {
              let read_on = discussion.read_on
              let discussionData = discussion.discussion
  
              if (!$utils.isDefined(read_on) && discussion.id != '0') {
                  containsUnread = true;
              }
              if ($utils.isDefined(discussionData) && discussion.id != '0') {
                  containsDiscussion = true;
              }
          }
          if (containsDiscussion) {
              if (containsUnread) {
                  $hub.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_multiple_20_regular";
                  $hub.toolbar.discussions.control.styles.setDestructiveClass();
              }
              else {
                  $hub.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_20_regular";
                  $hub.toolbar.discussions.control.styles.setDestructiveClass();
              }
          }
          else {
  
              $hub.toolbar.discussions.control.icon = "icon icon-ic_fluent_chat_empty_20_regular";
              $hub.toolbar.discussions.control.styles.setCreationClass();
  
          }
      }
  }
  else {
      $hub.toolbar.discussions.hidden = true;
  }
  }
  on_instructions_clicked(event = null) {
    return this.on_instructions_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_instructions_clickedInternal(
    $hub: FootprintManager_single_material_hubComponent,
  
    $shell: FootprintManager_ShellService,
    $datasources: FootprintManager_DatasourceService,
    $flows: FootprintManager_FlowService,
    $reports: FootprintManager_ReportService,
    $settings: SettingsValuesService,
    $operations: FootprintManager_OperationService,
    $utils: UtilsService,
    $context: FootprintManager_single_material_hub_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: FootprintManager_LocalizationService,
    $event: any
  ) {
  $shell.FootprintManager.openinstructions_gridDialog(
    {
      entityName: 'Material',
      enabled: true,
      entityKey1:$hub.inParams.materialId,
      lookup_code:$hub.vars.lookupCode
    },
    'flyout',EModalSize.Xlarge
  );
   
  }
  //#endregion private flows
}
